import { supabase } from '../lib/supabase';
const BUCKET = 'charger_firmware';
const TABLE = 'charger_firmware_versions';
export class ChargerFirmwareService {
    static async getLatestChargerFirmwareVersion() {
        const { data, error } = await supabase.rpc('get_latest_charger_firmware_version');
        if (error) {
            console.error('Error fetching latest charger firmware version:', error);
            throw new Error(error.message);
        }
        if (!data || data.length === 0)
            return null;
        return data[0];
    }
    static async checkForChargerUpdate(currentVersion) {
        const latest = await this.getLatestChargerFirmwareVersion();
        if (!latest || !currentVersion) {
            return {
                isUpdateAvailable: false,
                currentVersion,
                latestVersion: latest?.version ?? null,
                latestFirmware: latest
            };
        }
        const isNewer = this.isNewerVersion(latest.version, currentVersion);
        return {
            isUpdateAvailable: isNewer,
            currentVersion,
            latestVersion: latest.version,
            latestFirmware: latest
        };
    }
    static isNewerVersion(candidate, current) {
        const candidateParts = candidate.split('.').map(Number);
        const currentParts = current.split('.').map(Number);
        for (let i = 0; i < Math.max(candidateParts.length, currentParts.length); i++) {
            const a = candidateParts[i] || 0;
            const b = currentParts[i] || 0;
            if (a > b)
                return true;
            if (a < b)
                return false;
        }
        return false;
    }
    static async getChargerFirmwareDownloadUrl(filePath) {
        const { data, error } = await supabase.storage
            .from(BUCKET)
            .createSignedUrl(filePath, 3600);
        if (error) {
            console.error('Error creating charger firmware signed URL:', error);
            throw new Error(error.message);
        }
        return data.signedUrl;
    }
    static async getAllChargerFirmwareVersions() {
        const { data, error } = await supabase
            .from(TABLE)
            .select('*')
            .order('created_at', { ascending: false });
        if (error) {
            console.error('Error fetching charger firmware versions:', error);
            throw new Error(error.message);
        }
        return data || [];
    }
    static async createChargerFirmwareVersion(version, description, releaseNotes, filePath, fileSizeBytes, isReleased) {
        const { data, error } = await supabase
            .from(TABLE)
            .insert({
            version,
            description,
            release_notes: releaseNotes,
            file_path: filePath,
            file_size_bytes: fileSizeBytes,
            is_released: isReleased
        })
            .select()
            .single();
        if (error) {
            console.error('Error creating charger firmware version:', error);
            if (error.code === '23505') {
                throw new Error('A charger firmware version with this version number already exists');
            }
            throw new Error(error.message);
        }
        return data;
    }
    static async updateChargerFirmwareVersion(id, updates) {
        const { data, error } = await supabase
            .from(TABLE)
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) {
            console.error('Error updating charger firmware version:', error);
            throw new Error(error.message);
        }
        return data;
    }
    static async deleteChargerFirmwareVersion(id) {
        const { error } = await supabase
            .from(TABLE)
            .delete()
            .eq('id', id);
        if (error) {
            console.error('Error deleting charger firmware version:', error);
            throw new Error(error.message);
        }
    }
    static async uploadChargerFirmwareFile(file, path) {
        const { error } = await supabase.storage
            .from(BUCKET)
            .upload(path, file, {
            cacheControl: '3600',
            upsert: false
        });
        if (error) {
            console.error('Error uploading charger firmware file:', error);
            throw new Error(error.message);
        }
        return path;
    }
    static async deleteChargerFirmwareFile(path) {
        const { error } = await supabase.storage
            .from(BUCKET)
            .remove([path]);
        if (error) {
            console.error('Error deleting charger firmware file:', error);
            throw new Error(error.message);
        }
    }
}
